  import { useEffect, useState, useRef } from "react";
  import { Box, Chip, Typography, CircularProgress, Backdrop } from "@mui/material";

  import { useMaterialUIController, setMiniSidenav } from "context";
  import { MY_DASHBOARD_QUERY } from "graphql/mydashboard";
  import { fetchMyDashboard } from "../../services/mydashboard.service";
  import { useFilterContext } from "../../context/FilterContext";
  /* ---------------- FILTER BLOCK ---------------- */
  const FilterBlock = ({ title, items, activeFilters, onFilterClick, filterLoading }) => (
    <Box sx={{ flexShrink: 0 }}>
      <Typography sx={{ mb: "20px", fontFamily: "inter", fontSize: "12px", fontWeight: "200", color: "#ffffff", opacity: "50%" }}>{title}</Typography>

      <Box sx={{ display: "flex", gap: "12px", flexDirection: "column" }}>
        {items.map((item, index) => {
          const isActive = activeFilters[title]?.includes(item.label);

          // Selected = White chip background, Unselected = Plain text
          return isActive ? (
            // SELECTED STATE - White chip with blue text
            <Chip
              key={index}
              label={item.label}
              onClick={() => !filterLoading && onFilterClick(title, item.label)}
              sx={{
                '&.MuiChip-root': {
                  backgroundColor: '#fff !important',
                  color: '#0f5ea8 !important',
                  borderRadius: "4px !important",
                  height: "32px !important",
                  maxWidth: "fit-content !important",
                  border: 'none !important',
                  cursor: filterLoading ? 'wait' : 'pointer',
                  opacity: filterLoading ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  fontFamily: "Inter !important",
                  fontSize: "16px !important",
                  fontWeight: "200 !important",
                  color: "#1672B9 !important",
                  opacity: "100%",
                },
                '&.MuiChip-root:hover': {
                  backgroundColor: 'rgba(255,255,255,0.85) !important',
                  transform: 'scale(1.02)',
                },
                '& .MuiChip-label': {
                  padding: '0px 8px !important',
                  color: '#0f5ea8 !important',
                  fontSize: '16px !important',
                },
              }}
            />
          ) : (
            // UNSELECTED STATE - Plain text
            <Typography
              key={index}
              onClick={() => !filterLoading && onFilterClick(title, item.label)}
              sx={{
                fontFamily: "Inter !important",
                fontSize: "16px !important",
                fontWeight: "200 !important",
                color: "#ffffff !important",
                opacity: "100%",
                cursor: filterLoading ? 'wait' : 'pointer',
                opacity: filterLoading ? 0.6 : 1,
                // fontWeight: 'normal',
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              {item.label}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );

  /* ---------------- FILTER PANEL CONTENT ---------------- */
  const FilterPanelContent = ({ openFilters, activeFilters, onFilterClick, filterLoading , filterOptions }) => {

    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav } = controller;
    return (
      <Box
        className="FilterBlock-custom"
        sx={{
          width: "100%",

          color: "white",
          backdropFilter: "blur(16px)",
          boxShadow: openFilters ? "0px 6px 20px rgba(0,0,0,0.3)" : "none",
          height: openFilters ? "400px" : "0px",
          overflowY: "auto",
          background: "linear-gradient(135deg, #0E5BA0, #1E8CD0) !important",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            gap: "48px",
            background: "linear-gradient(135deg, #0E5BA0, #1E8CD0) !important",
            flexDirection: { xs: "column", md: "row" },
            padding: { xs: "16px", md: miniSidenav ? "20px 60px" : "20px 30px" },
            
            color: "#ffff",
          }}
        >
        {Object.entries(filterOptions || {}).map(([title, values]) => (
          <FilterBlock
            key={title}
            title={title}
            items={(values || []).map(v => ({
              label: v?.name || v
            }))}
            activeFilters={activeFilters}
            onFilterClick={onFilterClick}
            filterLoading={filterLoading}
          />
        ))}

        </Box>
      </Box>
    );
  };

  /* ---------------- FILTER PANEL WRAPPER ---------------- */
  const FilterPanelContainer = ({ openFilters, setOpenFilters, onFiltersChange, filterPresets, filterOptions: propFilterOptions }) => {
    const isApplyingPresetRef = useRef(false);
    const prevPresetsRef = useRef(null);

    // Initialize with preset if provided, otherwise empty
    const { activeFilters, setActiveFilters, filterOptions: contextFilterOptions } = useFilterContext();
    const [filterLoading, setFilterLoading] = useState(false);
    
    // Use prop filterOptions if provided, otherwise fall back to context
    const filterOptions = propFilterOptions || contextFilterOptions;

    // Apply filter presets when they change (only if different from previous)
    useEffect(() => {
      if (filterPresets) {
        // Deep comparison with sorted arrays to avoid unnecessary updates
        const comparePresets = (preset1, preset2) => {
          if (!preset1 || !preset2) return false;
          const keys = Object.keys(preset1);
          if (keys.length !== Object.keys(preset2).length) return false;

          return keys.every(key => {
            const arr1 = [...(preset1[key] || [])].sort();
            const arr2 = [...(preset2[key] || [])].sort();
            return arr1.length === arr2.length &&
              arr1.every((val, idx) => val === arr2[idx]);
          });
        };

        const presetsChanged = !prevPresetsRef.current || !comparePresets(prevPresetsRef.current, filterPresets);

        if (presetsChanged) {
          isApplyingPresetRef.current = true;
          setFilterLoading(true);

          setTimeout(() => {
            // Create a new object to ensure state update
            setActiveFilters({ ...filterPresets });
            prevPresetsRef.current = { ...filterPresets };
            setFilterLoading(false);
            isApplyingPresetRef.current = false;
          }, 100);
        }
      } else if (prevPresetsRef.current) {
        // If preset is cleared, reset to empty
        prevPresetsRef.current = null;
      }
    }, [filterPresets]);

    const handleFilterClick = (category, value) => {
      if (filterLoading) return;

      setFilterLoading(true);
      isApplyingPresetRef.current = false;

      // Simulate loading delay
      setTimeout(() => {
        setActiveFilters(prev => {
          const arr = prev[category] || [];
          const exists = arr.includes(value);

          const newFilters = {
            ...prev,
            [category]: exists
              ? arr.filter(v => v !== value)
              : [...arr, value]
          };

          // Callback to parent component if provided (only if not applying preset)
          if (onFiltersChange && !isApplyingPresetRef.current) {
            onFiltersChange(newFilters);
          }

          setFilterLoading(false);
          return newFilters;
        });
      }, 800);
    };


    useEffect(() => {
      if (openFilters) {
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
        document.body.style.overflowY = "scroll";
      } else {
        document.body.style.position = "";
        document.body.style.width = "";
        document.body.style.overflowY = "";
      }

      return () => {
        document.body.style.position = "";
        document.body.style.width = "";
        document.body.style.overflowY = "";
      };
    }, [openFilters]);

    return (
      <>
        {/* Full page loading backdrop */}
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: 9999,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
          }}
          open={filterLoading}
        >
          <CircularProgress sx={{ color: "#1672B9" }} size={50} />
        </Backdrop>

        <Box sx={{ position: "relative", width: "100%" }}>
          <Box
            sx={{
              position: "absolute",
              width: "100vw",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 50,
              overflowX: "auto",
              overflowY: "hidden",
              maxHeight: openFilters ? "400px" : 0,
              transition: "max-height 0.4s ease",
            }}
          >
            <FilterPanelContent
              openFilters={openFilters}
              activeFilters={activeFilters}
              onFilterClick={handleFilterClick}
              filterLoading={filterLoading}
              filterOptions={filterOptions}
            />
          </Box>
        </Box>

        {openFilters && (
          <Box
            onClick={() => setOpenFilters(false)}
            sx={{
              position: "fixed",
              top: "400px",
              left: 0,
              width: "100%",
              height: "calc(100vh - 400px)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              background: "rgba(255,255,255,0.35)",
              zIndex: 40,
              pointerEvents: "auto",
            }}
          />
        )}
      </>
    );
  };

  export default FilterPanelContainer;
